import React from "react";
import PropTypes from "prop-types";
// Material imports
import { ThemeProvider } from "@mui/material/styles";
import CopyIcon from "@mui/icons-material/FileCopyOutlined";
import ViewIcon from "@mui/icons-material/QuestionAnswer";
import ResetIcon from "@mui/icons-material/History";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
// Limitless imports
import { useNotificationContext, useAppConfig, useTranslation } from "@limitless/common/hooks";
import { useExpertProfileContext } from "@limitless/expert";
import ResponsiveDataTable from "../data-table/ResponsiveDataTable";
import { getTableActionColumnOptions } from "../../utils/TableActionCellUtil";
import HeaderIconButton from "../buttons/header-icon-button/HeaderIconButton";
import { calculateMessageState } from "../../utils/message-state-engine/MessageStateEngine";
import { validateMessageId } from "../../apis/MessageActions";
import { INITIAL_MESSAGE_RESOURCES } from "../../apis/MessageResourceActions";
import { getPendingQuestionHistoryTableData } from "./PendingQuestionHistoryHelper";
import ConversationDialog from "../dialogs/conversation-dialog/ConversationDialog";
import { modernTheme } from "@limitless/themeing/modern-theme/ModernTheme";
const PendingQuestionHistoryTable = ({
  loading = false,
  onReload,
  pendingQuestions = [],
  title = "",
  height = 320
}) => {
  const [message, setMessage] = React.useState();
  const {
    t
  } = useTranslation();
  const {
    addSuccessNotification
  } = useNotificationContext();
  const {
    formatDate,
    profile,
    isDarkMode
  } = useExpertProfileContext();
  const {
    countryList
  } = useAppConfig();
  const theme = React.useMemo(() => modernTheme(isDarkMode ? "dark" : "light"), [isDarkMode]);
  const customActionRenderer = (value, tableMeta) => {
    const dataIndex = tableMeta.rowData[tableMeta.rowData.length - 1];
    const record = pendingQuestions[dataIndex];
    return /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex"
      }
    }, /*#__PURE__*/React.createElement(HeaderIconButton, {
      buttonId: `copyTaskId${tableMeta.rowIndex}}`,
      tooltip: "Copy Task ID",
      onButtonClick: () => addSuccessNotification("Task ID copied to clipboard"),
      copyValue: record.messageId,
      copyToClipboardFeature: true,
      icon: /*#__PURE__*/React.createElement(CopyIcon, null)
    }), /*#__PURE__*/React.createElement(HeaderIconButton, {
      buttonId: `viewTask${tableMeta.rowIndex}}`,
      onButtonClick: () => validateMessageId(record.messageId, message => {
        if (message) {
          const messageState = calculateMessageState(profile, message, INITIAL_MESSAGE_RESOURCES);
          setMessage(messageState);
        }
      }),
      tooltip: "View Task",
      icon: /*#__PURE__*/React.createElement(ViewIcon, null)
    }));
  };
  const {
    data,
    columns
  } = getPendingQuestionHistoryTableData(pendingQuestions, value => formatDate(value, profile, countryList), t);
  columns.push(getTableActionColumnOptions(customActionRenderer));
  const options = {
    download: false,
    viewColumns: false,
    textLabels: {
      body: {
        noMatch: t("No tasks pending fee allocation")
      }
    },
    customToolbar: function renderReloadIcon() {
      return /*#__PURE__*/React.createElement(Tooltip, {
        title: t("Reload pending fee allocation tasks")
      }, /*#__PURE__*/React.createElement(IconButton, {
        onClick: onReload,
        size: "large"
      }, /*#__PURE__*/React.createElement(ResetIcon, null)));
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, React.useMemo(() => {
    return /*#__PURE__*/React.createElement(ResponsiveDataTable, {
      height: height,
      title: title,
      tableId: "pendingPaymentTasksTable",
      data: data,
      columns: columns,
      options: options,
      loading: loading,
      fullscreen: true
    });
  }, [loading, pendingQuestions]), message && /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(ConversationDialog, {
    headerText: "Conversation",
    message: message,
    onCancelClick: () => setMessage(),
    buttonVariant: "contained",
    buttonColor: "secondary"
  })));
};
export default PendingQuestionHistoryTable;
PendingQuestionHistoryTable.propTypes = {
  pendingQuestions: PropTypes.array,
  onReload: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  title: PropTypes.string,
  height: PropTypes.number
};